<template>
  <BarChart
    :data="calculatedData"
    :options="chartOptions"
  />
</template>

<script setup lang="ts">
import { Chart as ChartJS, registerables } from "chart.js"
import { Bar as BarChart } from "vue-chartjs"
import type { ChartOptions, ChartData } from "chart.js"
import "chartjs-adapter-date-fns"

const { formatToCurrencyString } = useCurrency()

type DateRange = {
  start: Date
  end: Date
}

ChartJS.register(...registerables)

const props = withDefaults(
  defineProps<{
    id: string
    datasets: {
      label: string
      data: number[]
      color?: string | undefined
    }[]
    labels: DateRange[]
    granularity?: Granularity | undefined
    currency: string
    isolatedDatasetIndex?: number | null
    hoveredDatasetIndex?: number | null
  }>(),
  {
    granularity: undefined,
    isolatedDatasetIndex: null,
    hoveredDatasetIndex: null,
  }
)

const dimColor = (
  color: string | undefined
): string | undefined =>
  color?.replace(/^rgb\((.+)\)$/, "rgb($1 / 0.15)")

const calculatedDatasets = computed(() =>
  props.datasets.map((dataset, index) => ({
    label: dataset.label,
    data: dataset.data,
    backgroundColor:
      props.hoveredDatasetIndex !== null &&
      props.hoveredDatasetIndex !== index
        ? dimColor(dataset.color)
        : dataset.color,
    hidden:
      props.isolatedDatasetIndex !== null &&
      props.isolatedDatasetIndex !== index,
  }))
)

const calculatedLabels = computed(() =>
  props.labels.map(({ start }) => start)
)

const calculatedData = computed<
  ChartData<"bar", number[], Date>
>(() => ({
  labels: calculatedLabels.value,
  datasets: calculatedDatasets.value,
}))

const colorText = useCssVar("--color-on-background")

const chartOptions = computed<ChartOptions<"bar">>(() => ({
  maintainAspectRatio: false,
  animation: false,
  responsive: true,
  plugins: {
    legend: { display: false },
    tooltip: {
      mode: "index",
      intersect: false,
      padding: 12,
      cornerRadius: 8,
      usePointStyle: true,
      boxWidth: 8,
      boxHeight: 8,
      filter: (item) => !item.dataset.hidden,
      callbacks: {
        title: (items) => {
          const item = items[0]
          if (!item) return ""
          const label = props.labels[item.dataIndex]
          if (!label) return ""
          return DateFns.format(label.start, "d MMM yyyy")
        },
        label: (item) => {
          const value =
            typeof item.raw === "number" ? item.raw : 0
          return ` ${item.dataset.label}: ${formatToCurrencyString(value, props.currency)}`
        },
        footer: (items) => {
          if (items.length <= 1) return ""
          const total = items.reduce(
            (sum, item) =>
              sum +
              (typeof item.raw === "number" ? item.raw : 0),
            0
          )
          return `Total: ${formatToCurrencyString(total, props.currency)}`
        },
      },
    },
  },
  datasets: {
    bar: { borderRadius: 0 },
  },
  scales: {
    x: {
      stacked: true,
      type: "timeseries",
      time: {
        displayFormats: { month: "MMM" },
        unit: props.granularity,
        isoWeekday: true,
      },
      ticks: {
        font: { size: 12 },
        color: `rgb(${colorText.value} / 0.7)`,
        minRotation: 0,
        maxRotation: 0,
        padding: 8,
        align: "center",
        autoSkipPadding: 8,
        includeBounds: false,
      },
      grid: {
        color: `rgb(${colorText.value} / 0.05)`,
        drawTicks: false,
      },
    },
    y: {
      stacked: true,
      beginAtZero: true,
      suggestedMax: 10000,
      grid: { display: false, drawTicks: false },
      ticks: {
        align: "center",
        font: { size: 12 },
        callback: (value) =>
          value !== 0
            ? formatToCurrencyString(
                Number(value),
                props.currency,
                {
                  compact: true,
                  stripTrailingZeros: true,
                }
              )
            : "",
      },
    },
  },
}))
</script>
